import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { AppLayout } from '../../components/layout/AppLayout';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import {
  ordersApi,
  OrderStatus,
  ORDER_STATUS_LABEL,
  VendorOrder,
} from '../../api/orders';
import { formatNgn, formatDateTime } from '../../utils/format';
import { OrderDetailDrawer } from './OrderDetailDrawer';

const STATUS_VARIANT: Record<
  OrderStatus,
  'success' | 'warning' | 'danger' | 'info' | 'neutral'
> = {
  placed: 'info',
  processing: 'info',
  shipped: 'info',
  out_for_delivery: 'info',
  delivered: 'success',
  completed: 'success',
  cancelled: 'danger',
  disputed: 'danger',
};

export const Orders: React.FC = () => {
  const toast = useToast();
  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [selected, setSelected] = useState<VendorOrder | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ordersApi.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setOrders(data.items);
    } catch (err) {
      toast.error(err, 'Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  function handleOrderUpdated(updated: VendorOrder) {
    setOrders((rows) => rows.map((o) => (o.id === updated.id ? updated : o)));
    setSelected(updated);
  }

  return (
    <AppLayout>
      <PageHeader
        title="Orders"
        description="Manage fulfillment. Once an order is marked delivered, the buyer confirms receipt and PayIQ releases the escrow to your balance."
      />

      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {(['all', ...Object.keys(ORDER_STATUS_LABEL)] as Array<
            OrderStatus | 'all'
          >).map((s) => {
            const active = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 13,
                  fontWeight: 500,
                  border: `1px solid ${
                    active ? 'var(--primary)' : 'var(--border)'
                  }`,
                  background: active ? 'var(--blue-100)' : 'var(--bg-card)',
                  color: active ? 'var(--primary)' : 'var(--text-secondary)',
                }}
              >
                {s === 'all' ? 'All' : ORDER_STATUS_LABEL[s as OrderStatus]}
              </button>
            );
          })}
        </div>
      </Card>

      <Card padding={0}>
        {loading ? (
          <div
            style={{
              padding: 48,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            title="No orders yet"
            description="When buyers place orders, they'll show up here."
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 14,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: 'var(--bg-muted)',
                    color: 'var(--text-secondary)',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  <th style={th}>Order</th>
                  <th style={th}>Items</th>
                  <th style={th}>Total</th>
                  <th style={th}>Status</th>
                  <th style={th}>Placed</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => setSelected(o)}
                    style={{
                      borderTop: '1px solid var(--border)',
                      cursor: 'pointer',
                    }}
                  >
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{o.reference}</div>
                      <div
                        style={{ fontSize: 12, color: 'var(--text-muted)' }}
                      >
                        {o.shippingAddress?.fullName ?? '—'}
                      </div>
                    </td>
                    <td style={td}>
                      {o.items.length} {o.items.length === 1 ? 'item' : 'items'}
                    </td>
                    <td style={td}>{formatNgn(o.total)}</td>
                    <td style={td}>
                      <Badge variant={STATUS_VARIANT[o.status]}>
                        {ORDER_STATUS_LABEL[o.status]}
                      </Badge>
                    </td>
                    <td style={td}>{formatDateTime(o.placedAt)}</td>
                    <td style={{ ...td, color: 'var(--text-muted)' }}>
                      <ChevronRight size={16} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected ? (
        <OrderDetailDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleOrderUpdated}
        />
      ) : null}
    </AppLayout>
  );
};

const th: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 600,
};
const td: React.CSSProperties = {
  padding: '14px 16px',
  verticalAlign: 'middle',
};
